import { useState, useCallback, useMemo } from 'react'

export type SearchableRecord = Record<string, any>

export function useTableSearch<T extends SearchableRecord>(
  data: T[],
  searchableColumns: (keyof T)[],
) {
  const [searchTerm, setSearchTerm] = useState('')

  const normalizeText = useCallback((text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }, [])

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(normalizeText(term.trim()))
    },
    [normalizeText],
  )

  const filteredData = useMemo(() => {
    if (!searchTerm) return data

    return data.filter((item) => {
      return searchableColumns.some((column) => {
        const value = item[column]
        if (value == null) return false

        const normalizedValue = normalizeText(String(value))
        return normalizedValue.includes(searchTerm)
      })
    })
  }, [data, searchTerm, searchableColumns, normalizeText])

  const searchInfo = useMemo(() => {
    return {
      totalResults: filteredData.length,
      isFiltering: searchTerm !== '',
      hasResults: filteredData.length > 0,
    }
  }, [filteredData.length, searchTerm])

  return {
    searchTerm,
    filteredData,
    handleSearch,
    searchInfo,
  }
}
